import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { Dialog } from '../../components/dialog/Dialog';
import { DialogContextControllerProps, DialogContextStateType } from './DialogContextController.types';

export const DialogContextState = createContext<DialogContextStateType | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const DialogContextDispatch = createContext<(data: DialogContextStateType) => void>(() => {});

export const DialogContextController = ({ children }: DialogContextControllerProps) => {
  const [dialog, setDialog] = useState<DialogContextStateType>({ open: false });
  const [dialogQueue, setDialogQueue] = useState<DialogContextStateType[]>([]);

  const {
    open,
    dialogContent,
    variant,
    title,
    classes,
    disablePortal,
    disableBackdropClick,
    fullScreenProps,
    hideCloseButton,
    customHeader,
    byQueue,
    onClose,
    ...rest
  } = useMemo(() => dialog, [dialog]);

  const handleClose = useCallback(() => {
    setDialog({ open: false });
  }, []);

  const handleSetDialog = useCallback(
    (data: DialogContextStateType) => {
      if (data.byQueue && data.open) {
        if (data.byQueue === byQueue) {
          setDialog(data);

          return;
        }

        const queueIndex = dialogQueue.findIndex(item => item.byQueue === data.byQueue);

        if (queueIndex >= 0) {
          const newQueue = [...dialogQueue];
          newQueue[queueIndex] = data;

          setDialogQueue(newQueue);
        } else {
          setDialogQueue(dialogQueue.concat([data]));
        }
      } else {
        setDialog(data);
      }
    },
    [byQueue, dialogQueue],
  );

  useEffect(() => {
    if (dialogQueue.length && !open) {
      const params = { ...dialogQueue[0] };

      setDialog(params);
      setDialogQueue(prev => prev.filter((_, index) => !!index));
    }
  }, [dialogQueue, open]);

  return (
    <DialogContextState.Provider value={dialog}>
      <DialogContextDispatch.Provider value={handleSetDialog}>
        <Dialog
          open={open}
          onClose={onClose || handleClose}
          variant={variant}
          title={title}
          classes={classes}
          disablePortal={disablePortal}
          disableBackdropClick={disableBackdropClick}
          fullScreenProps={fullScreenProps}
          hideCloseButton={hideCloseButton}
          customHeader={customHeader}
          {...rest}
        >
          {dialogContent}
        </Dialog>

        {children}
      </DialogContextDispatch.Provider>
    </DialogContextState.Provider>
  );
};
